import React, { useContext, useEffect, useState } from 'react';
import { css } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { UserContext } from '../context/UserContext';
import { getCommunityLoginUrl } from '@atlassian/community-library';
import { getEnvironmentAsEnum } from '../utils/environment';
import { PageWithAnalytics } from '../components/common/analytics-wrapped/PageWithAnalytics';
import DocumentHead from '../components/common/DocumentHead';
import { DEFAULT_CHAMPIONS_SIGNUP_URL } from '../utils/constants';
import { championsAuthSignupIsActive } from '../services/statsig/featureGates';
import ChampionApplicationForm from '../components/champions/ChampionApplicationForm';
import { getProfileInternalId, getUserAccountStatuses } from '../services/http/ChampionGraphQlOperations';

const TITLE = 'Champions Apply | Atlassian Community';
const DESCRIPTION = 'Apply to become an Atlassian Champion.';
const CHAMPIONS_APPLY_PATH = '/champions/apply';

const getApplyRedirectUrl = () => `${process.env.HOME_BASE_URL}${CHAMPIONS_APPLY_PATH}`;

type AccountInfo = {
  emailId: string;
  profileId: string;
  isManaged: boolean;
};

export const ChampionsApplyPage: React.FC = () => {
  const { user, userIsInitialized } = useContext(UserContext) ?? {};
  const isLoggedIn = !!user;
  const isAuthSignupEnabled = championsAuthSignupIsActive();
  const [accountInfo, setAccountInfo] = useState<AccountInfo | null>(null);
  const [accountError, setAccountError] = useState(false);
  const [accountLoading, setAccountLoading] = useState(true);
  const homepage =
    process.env['REACT_APP_ENVI'] === 'local' ? 'http://localhost:9000' : process.env['HOME_BASE_URL'];

  useEffect(() => {
    if (!isAuthSignupEnabled) {
      window.location.href = DEFAULT_CHAMPIONS_SIGNUP_URL;
      return;
    }
    if (!userIsInitialized || !isLoggedIn) {
      window.location.href = getCommunityLoginUrl(
        { redirectUrl: getApplyRedirectUrl(), experience: 'entry-auth' },
        getEnvironmentAsEnum(),
      );
      return;
    }
    if (!user?.account_id) {
      setAccountError(true);
      setAccountLoading(false);
      return;
    }
    let cancelled = false;
    const fetchAccountInfo = async () => {
      try {
        const response = await getUserAccountStatuses(user.account_id, homepage, homepage);
        const account = response?.data?.unified?.account;
        if (
          cancelled ||
          !account ||
          'code' in account ||
          !account.emailId
        ) {
          if (!cancelled) {
            setAccountError(true);
            setAccountLoading(false);
          }
          return;
        }

        const profileId = await getProfileInternalId(user.account_id, homepage, homepage);

        if (cancelled) return;

        if (!profileId) {
          setAccountError(true);
          setAccountLoading(false);
          return;
        }

        setAccountInfo({
          emailId: account.emailId,
          profileId,
          isManaged: account.isManaged === true,
        });
        setAccountLoading(false);
      } catch {
        if (!cancelled) {
          setAccountError(true);
          setAccountLoading(false);
        }
      }
    };
    fetchAccountInfo();
    return () => {
      cancelled = true;
    };
  }, [isAuthSignupEnabled, userIsInitialized, isLoggedIn, user?.account_id, homepage]);

  const canShowContent = isAuthSignupEnabled && userIsInitialized && isLoggedIn;

  if (!canShowContent || accountLoading) {
    return (
      <div css={centeredPageStyles}>
        <div css={containerStyles}>
          <p css={descriptionStyles}>Loading...</p>
        </div>
      </div>
    );
  }

  if (accountError || !accountInfo) {
    return (
      <div css={pageStyles}>
        <div css={containerStyles}>
          <p css={errorBannerStyles}>Oops! Something went wrong.</p>
        </div>
      </div>
    );
  }

  return (
    <PageWithAnalytics eventScreenName="championsApplyScreen">
      <DocumentHead title={TITLE} description={DESCRIPTION} />
      <div css={pageStyles}>
        <div style={{ width: '100%' }}>
          <ChampionApplicationForm
            aaid={user?.account_id ?? ''}
            accountIsManaged={accountInfo.isManaged}
            emailId={accountInfo.emailId}
            profileId={accountInfo.profileId}
            userName={user?.name ?? ''}
            homeBaseUrl={homepage ?? ''}
          />
        </div>
      </div>
    </PageWithAnalytics>
  );
};

export default ChampionsApplyPage;

const containerStyles = css({
  maxWidth: '800px',
  minWidth: '375px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: token('space.600'),
});

const descriptionStyles = css({
  marginBottom: token('space.200'),
  color: token('color.text'),
  fontFamily: 'Inter',
  fontWeight: 400,
  lineHeight: '24px',
  textAlign: 'center',
  fontSize: '18px',
});

const pageStyles = css({
  display: 'flex',
  width: '100%',
  flexDirection: 'column',
  alignItems: 'center',
  paddingTop: token('space.400'), /* half distance from navbar */
  paddingBottom: token('space.800'),
  minHeight: '400px',
});

const centeredPageStyles = css({
  display: 'flex',
  width: '100%',
  minHeight: '400px',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  paddingTop: token('space.400'),
  paddingBottom: token('space.800'),
});

const errorBannerStyles = css({
  color: '#3e3e3c',
  fontSize: '18px',
  fontWeight: 400,
  textAlign: 'center',
  lineHeight: '24px',
});

